import React from 'react';
import { getContrastRatio, getWCAGLevel } from '../utils/colorAnalysis';

export interface ColorSwatch {
  hex: string;
  label: string;
  textColor?: 'white' | 'black';
}

interface ColorDemoBlockProps {
  colors: ColorSwatch[];
  caption?: string;
  layout?: 'row' | 'comparison';
  contrastInfo?: { ratio: string; level: 'AAA' | 'AA' | 'FAIL' };
}

function getAutoTextColor(hex: string): 'white' | 'black' {
  return getContrastRatio(hex, '#000000') >= getContrastRatio(hex, '#FFFFFF') ? 'black' : 'white';
}

const LEVEL_STYLES: Record<string, string> = {
  AAA: 'bg-green-100 text-green-800',
  AA: 'bg-brand-100 text-brand-800',
  FAIL: 'bg-red-100 text-red-800',
};

const ColorDemoBlock: React.FC<ColorDemoBlockProps> = ({
  colors,
  caption,
  layout = 'row',
  contrastInfo: suppliedInfo,
}) => {
  const ratio = colors.length === 2 && suppliedInfo ? getContrastRatio(colors[0].hex, colors[1].hex) : null;
  const contrastInfo = ratio === null ? undefined : { ratio: ratio.toFixed(3) + ':1', level: getWCAGLevel(ratio) };
  return (
  <figure className="my-6 rounded-xl border border-surface-border overflow-hidden not-prose">
    <div className={`flex ${layout === 'comparison' && colors.length === 2 ? '' : 'flex-wrap'}`}>
      {colors.map((swatch, i) => {
        const textColor = swatch.textColor ?? getAutoTextColor(swatch.hex);
        return (
          <div
            key={i}
            className="flex-1 min-w-[90px] h-24 flex flex-col items-center justify-center gap-1 px-3"
            style={{ backgroundColor: swatch.hex }}
          >
            <span
              className="text-sm font-bold font-mono tracking-wide"
              style={{ color: textColor === 'white' ? '#ffffff' : '#000000' }}
            >
              {swatch.hex.toUpperCase()}
            </span>
            <span
              className="text-xs text-center leading-tight"
              style={{ color: textColor === 'white' ? '#ffffff' : '#000000' }}
            >
              {swatch.label}
            </span>
          </div>
        );
      })}
    </div>
    {(caption || contrastInfo) && (
      <div className="bg-surface px-4 py-2.5 flex items-center justify-between gap-4 border-t border-surface-border">
        {caption && (
          <figcaption className="text-xs text-ink-secondary leading-relaxed">{caption}</figcaption>
        )}
        {contrastInfo && (
          <div className="flex items-center gap-2 flex-shrink-0">
            <span className="text-xs text-ink-muted">Pair, normal text:</span>
            <span className="text-xs font-mono font-semibold text-ink-secondary">{contrastInfo.ratio}</span>
            <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${LEVEL_STYLES[contrastInfo.level]}`}>
              {contrastInfo.level}
            </span>
          </div>
        )}
      </div>
    )}
  </figure>
);
};

export default ColorDemoBlock;
